import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PortalAuthHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    // If no token but email is provided, pre-fill login form
    if (!token && email) {
      navigate(`/portal/login?email=${encodeURIComponent(email)}`, { replace: true });
      return;
    }

    // If no token at all, redirect to login with error
    if (!token) {
      navigate('/portal/login?error=invalid', { replace: true });
      return;
    }

    // Validate token via API call (goes through proxy)
    const validateToken = async () => {
      try {
        console.log('[PortalAuthHandler] Validating token via:', `${BASE_URL}/api/portal/validate-token`);

        // Call a new endpoint to validate the token and create session
        const response = await fetch(`${BASE_URL}/api/portal/validate-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });

        const data = await response.json();
        console.log('[PortalAuthHandler] Token validation response:', data);

        if (response.ok && data.success) {
          // Token valid and session created, redirect to dashboard
          console.log('[PortalAuthHandler] Session created, redirecting to dashboard');
          navigate('/portal/dashboard', { replace: true });
        } else {
          // Token invalid or expired
          console.error('[PortalAuthHandler] Token validation failed:', data.error);
          navigate('/portal/login?error=expired', { replace: true });
        }
      } catch (err) {
        console.error('[PortalAuthHandler] Error validating token:', err);
        navigate('/portal/login?error=server', { replace: true });
      }
    };

    validateToken();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate('/portal/login')} style={styles.button}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner}></div>
        <p style={styles.text}>Verifying your login link...</p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 20px'
  },
  text: {
    color: '#6b7280',
    fontSize: '16px',
    margin: 0
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  errorText: {
    color: '#dc2626',
    fontSize: '16px',
    marginBottom: '20px'
  },
  button: {
    background: '#f97316',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

// Add CSS animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('#portal-auth-styles')) {
  styleSheet.id = 'portal-auth-styles';
  document.head.appendChild(styleSheet);
}

export default PortalAuthHandler;
